import { useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./GifteeBudgetRangeSlider.module.css";

const BUDGET_MIN = 10;
const BUDGET_MAX = 200;
const BUDGET_GAP = 10;

/**
 * Dual-handle budget slider — same nonlinear scale and visuals as AddGifteeWizard Budget step.
 *
 * Props:
 * @param budgetMin budgetMax — dollars, step 5, range 10–200+
 * @param onMinChange onMaxChange — receive a number (not a DOM event); parent clamps if needed.
 * @param fmtBudget optional (v: number) => string
 */
export default function GifteeBudgetRangeSlider({
  budgetMin,
  budgetMax,
  onMinChange,
  onMaxChange,
  fmtBudget: fmtBudgetProp,
}) {
  const [activeBudgetThumb, setActiveBudgetThumb] = useState(null);

  const fmtBudget =
    typeof fmtBudgetProp === "function" ? fmtBudgetProp : fmtBudgetSingle;

  const sliderStops = [
    { value: 10, pct: 0 },
    { value: 50, pct: (45.5 / 345) * 100 },
    { value: 120, pct: (244.5 / 345) * 100 },
    { value: 200, pct: 100 },
  ];

  const getSliderSegment = (value, key) => {
    const firstStop = sliderStops[0];
    const lastStop = sliderStops[sliderStops.length - 1];
    if (value <= firstStop[key]) return [firstStop, sliderStops[1]];
    if (value >= lastStop[key]) return [sliderStops[sliderStops.length - 2], lastStop];
    const startIndex = sliderStops.findIndex((stop, index) => {
      const nextStop = sliderStops[index + 1];
      return nextStop && value >= stop[key] && value <= nextStop[key];
    });
    return [sliderStops[startIndex], sliderStops[startIndex + 1]];
  };

  const valueToVisualPct = (value) => {
    const [start, end] = getSliderSegment(value, "value");
    const ratio = (value - start.value) / (end.value - start.value);
    return start.pct + ratio * (end.pct - start.pct);
  };

  const pctToValue = (pct) => {
    const [start, end] = getSliderSegment(pct, "pct");
    const ratio = (pct - start.pct) / (end.pct - start.pct);
    return Math.round((start.value + ratio * (end.value - start.value)) / 5) * 5;
  };

  const displayMinPct = valueToVisualPct(budgetMin);
  const displayMaxPct = valueToVisualPct(budgetMax);
  const fillLeftPct = budgetMin === 50 ? (49 / 345) * 100 : displayMinPct;
  const fillRightPct = budgetMax === 120 ? (244 / 345) * 100 : displayMaxPct;

  const valueFromPointer = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
    return pctToValue((x / rect.width) * 100);
  };

  const updateBudgetFromPointer = (event, thumb = activeBudgetThumb) => {
    if (!thumb) return;
    const nextValue = valueFromPointer(event);
    if (thumb === "min") onMinChange(nextValue);
    else onMaxChange(nextValue);
  };

  const handleBudgetPointerDown = (event) => {
    event.preventDefault();
    const nextValue = valueFromPointer(event);
    const nextThumb =
      Math.abs(nextValue - budgetMin) <= Math.abs(nextValue - budgetMax) ? "min" : "max";
    setActiveBudgetThumb(nextThumb);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    updateBudgetFromPointer(event, nextThumb);
  };

  const handleBudgetPointerUp = (event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setActiveBudgetThumb(null);
  };

  const commitTypedMin = (rawDigits) => {
    const parsed = Number.parseInt(rawDigits, 10);
    if (!Number.isFinite(parsed)) {
      onMinChange(budgetMin);
      return;
    }
    const clamped = Math.min(Math.max(parsed, BUDGET_MIN), budgetMax - BUDGET_GAP);
    onMinChange(clamped);
  };

  const commitTypedMax = (rawDigits) => {
    const parsed = Number.parseInt(rawDigits, 10);
    if (!Number.isFinite(parsed)) {
      onMaxChange(budgetMax);
      return;
    }
    const clamped = Math.max(Math.min(parsed, BUDGET_MAX), budgetMin + BUDGET_GAP);
    onMaxChange(clamped);
  };

  return (
    <div className={styles.controls}>
      <div className={styles.display} aria-live="polite">
        <BudgetDisplayValue
          value={budgetMin}
          formatted={fmtBudget(budgetMin)}
          ariaLabel="Minimum budget"
          onCommit={commitTypedMin}
        />
        <span className={styles.displayDivider} aria-hidden="true" />
        <BudgetDisplayValue
          value={budgetMax}
          formatted={fmtBudget(budgetMax)}
          ariaLabel="Maximum budget"
          onCommit={commitTypedMax}
        />
      </div>
      <div className={styles.sliderGroup}>
        <div
          role="presentation"
          className={styles.sliderWrap}
          onPointerDown={handleBudgetPointerDown}
          onPointerMove={(event) => updateBudgetFromPointer(event)}
          onPointerUp={handleBudgetPointerUp}
          onPointerCancel={handleBudgetPointerUp}
          style={{
            "--budget-min-pct": `${displayMinPct}%`,
            "--budget-max-pct": `${displayMaxPct}%`,
            "--budget-fill-left": `${fillLeftPct}%`,
            "--budget-fill-width": `${Math.max(fillRightPct - fillLeftPct, 0)}%`,
          }}
        >
          <div className={styles.sliderTrack}>
            <div className={styles.sliderFill} />
          </div>
          <span className={`${styles.budgetThumb} ${styles.budgetThumbMin}`} aria-hidden="true" />
          <span className={`${styles.budgetThumb} ${styles.budgetThumbMax}`} aria-hidden="true" />
          <input
            type="range"
            min={10}
            max={200}
            step={5}
            value={budgetMin}
            onChange={(e) => onMinChange(Number(e.target.value))}
            className={styles.rangeInput}
            aria-label="Minimum budget"
          />
          <input
            type="range"
            min={10}
            max={200}
            step={5}
            value={budgetMax}
            onChange={(e) => onMaxChange(Number(e.target.value))}
            className={styles.rangeInput}
            aria-label="Maximum budget"
          />
        </div>
        <div className={styles.sliderLabels}>
          <span>$10</span>
          <span>$200+</span>
        </div>
      </div>
    </div>
  );
}

function BudgetDisplayValue({ value, formatted, ariaLabel, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [inputWidth, setInputWidth] = useState(0);
  const inputRef = useRef(null);
  const sizerRef = useRef(null);
  const skipCommitRef = useRef(false);

  const sizerText = editing ? `$${draft}` : formatted;

  useLayoutEffect(() => {
    if (!sizerRef.current) return;
    const measured = sizerRef.current.getBoundingClientRect().width;
    setInputWidth(Math.max(Math.ceil(measured), 1));
  }, [sizerText]);

  useEffect(() => {
    if (!editing || !inputRef.current) return;
    inputRef.current.focus();
    inputRef.current.select();
  }, [editing]);

  const startEditing = () => {
    skipCommitRef.current = false;
    setDraft(String(value));
    setEditing(true);
  };

  const finishEditing = () => {
    setEditing(false);
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      return;
    }
    onCommit(draft);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      inputRef.current?.blur();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      skipCommitRef.current = true;
      inputRef.current?.blur();
    }
  };

  const handleChange = (event) => {
    setDraft(event.target.value.replace(/\D/g, "").slice(0, 4));
  };

  return (
    <span className={styles.displayValueWrap}>
      <span ref={sizerRef} className={styles.displaySizer} aria-hidden="true">
        {sizerText}
      </span>
      {editing ? (
        <span className={styles.displayEditing} style={{ width: inputWidth }}>
          <span aria-hidden="true">$</span>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className={styles.displayInput}
            value={draft}
            aria-label={ariaLabel}
            onChange={handleChange}
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
          />
        </span>
      ) : (
        <button
          type="button"
          className={styles.displayButton}
          style={{ width: inputWidth }}
          aria-label={`${ariaLabel}, ${formatted}. Tap to type an exact amount.`}
          onClick={startEditing}
        >
          {formatted}
        </button>
      )}
    </span>
  );
}

function fmtBudgetSingle(v) {
  return v >= 200 ? "$200+" : `$${v}`;
}
